import { useState, useEffect } from 'react';
import '../styles/GradeModal.css';
import GradeFormatFields from './GradeFormatFields';
import {
    gradeValueToEditString,
    resolveRowGradeFormat,
} from '../utils/gradeValues';
import { extractErrorMessage, getFetchErrorMessage } from '../utils/errorUtils';

const AssignmentModal = ({ onClose }) => {
    const [module_id, setModule_id] = useState('');
    const [modules, setModules] = useState([]);
    const [allStudents, setAllStudents] = useState([]); // All students fetched from API
    const [students, setStudents] = useState([]); // Filtered students by group
    const [groups, setGroups] = useState([]); // Groups for the selected module
    const [selectedGroupCode, setSelectedGroupCode] = useState(''); // Selected group filter
    const [studentGrades, setStudentGrades] = useState({}); // { student_id: { final_grade, is_passed, date } }
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        async function fetchModules() {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${process.env.REACT_APP_API_URL}/lecturer/grades/modules`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setModules(data);
                } else {
                    console.error("Error fetching modules:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching modules:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchModules();
    }, []);

    // Fetch groups for the selected module
    useEffect(() => {
        async function fetchGroups() {
            if (!module_id) {
                setGroups([]);
                setSelectedGroupCode('');
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/courses/modules/${module_id}/groups`
                );

                if (response.ok) {
                    const data = await response.json();
                    setGroups(data || []);
                } else {
                    console.error("Error fetching groups:", response.statusText);
                    setGroups([]);
                }
            } catch (error) {
                console.error("Error fetching groups:", error);
                setGroups([]);
            }
        }

        fetchGroups();
    }, [module_id]);

    // Fetch students and their existing final grades when module is selected
    useEffect(() => {
        async function fetchStudentsWithGrades() {
            if (!module_id) {
                setAllStudents([]);
                setStudents([]);
                setStudentGrades({});
                return;
            }

            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/lecturer/grades/studentsWithFinalGrades?module_id=${module_id}`,
                    {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${token}`
                        }
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    setAllStudents(data.students || []);
                    
                    // Initialize studentGrades with existing grades
                    const gradesMap = {};
                    data.students?.forEach(student => {
                        const fmt = resolveRowGradeFormat({ grade: student.final_grade, grade_format: student.grade_format });
                        gradesMap[student.student_id] = {
                            final_grade: student.final_grade != null && student.final_grade !== ''
                                ? gradeValueToEditString(student.final_grade, fmt)
                                : '',
                            grade_format: fmt,
                            is_passed: student.is_passed || false,
                            date: student.date || ''
                        };
                    });
                    setStudentGrades(gradesMap);
                } else {
                    console.error("Error fetching students:", response.statusText);
                    setAllStudents([]);
                    setStudents([]);
                }
            } catch (error) {
                console.error("Error fetching students:", error);
                setAllStudents([]);
                setStudents([]);
            }
        }

        fetchStudentsWithGrades();
    }, [module_id]);

    // Filter students by selected group
    useEffect(() => {
        if (!selectedGroupCode) {
            // Show all students if no group is selected
            setStudents(allStudents);
        } else {
            // Filter students by selected group
            const filtered = allStudents.filter(
                student => student.group_code === selectedGroupCode
            );
            setStudents(filtered);
        }
    }, [selectedGroupCode, allStudents]);

    const handleModuleChange = (e) => {
        setModule_id(e.target.value);
        setSelectedGroupCode('');
        setStudentGrades({});
    };

    const handleGroupChange = (e) => {
        setSelectedGroupCode(e.target.value);
    };

    const handleGradeChange = (studentId, field, value) => {
        setStudentGrades(prev => {
            const newData = {
                ...prev,
                [studentId]: {
                    ...prev[studentId] || {},
                    [field]: field === 'is_passed' ? value === 'true' || value === true : value
                }
            };

            if (field === 'final_grade' && value) {
                const fmt = newData[studentId].grade_format || 'scale_5';
                if (fmt === 'pass_fail') {
                    newData[studentId].is_passed = value === 'PASSED';
                } else {
                    const gradeNum = parseFloat(value);
                    if (!isNaN(gradeNum)) {
                        newData[studentId].is_passed = fmt === 'percent' ? gradeNum >= 60 : gradeNum >= 3.0;
                    }
                }
            }
            if (field === 'grade_format') {
                newData[studentId].final_grade = '';
            }

            // Auto-set date to today if not set
            if (field === 'final_grade' && value && !newData[studentId].date) {
                newData[studentId].date = new Date().toISOString().split('T')[0];
            }

            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!module_id) {
            setErrorMsg('Wybierz przedmiot.');
            return;
        }

        // Check if at least one student has a grade
        const hasGrades = Object.values(studentGrades).some(
            grade => grade.final_grade && grade.final_grade !== ''
        );

        if (!hasGrades) {
            setErrorMsg('Wprowadź co najmniej jedną ocenę końcową.');
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/lecturer/grades/addFinalGrades`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    module_id: module_id,
                    grades: Object.entries(studentGrades)
                        .filter(([_, grade]) => grade.final_grade && grade.final_grade !== '')
                        .map(([student_id, grade]) => ({
                            student_id: parseInt(student_id),
                            final_grade: grade.final_grade,
                            grade_format: grade.grade_format,
                            date: grade.date || new Date().toISOString().split('T')[0]
                        }))
                })
            });

            if (!response.ok) {
                const msg = await getFetchErrorMessage(response, 'Nie udało się zapisać ocen końcowych.');
                throw new Error(msg);
            }

            // Reset form
            setModule_id('');
            setStudents([]);
            setStudentGrades({});
            onClose();
        } catch (error) {
            console.error("Błąd podczas zapisu:", error);
            setErrorMsg(extractErrorMessage(error));
        }
    };

    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <p>Ładowanie danych...</p>
                </div>
            </div>
        );
    }

    const errorBanner = errorMsg ? (
        <div className="form-error-banner" role="alert">
            {errorMsg}
        </div>
    ) : null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-content final-grades-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="exam-type-header">
                    <h3 className="modal-title">Dodaj oceny końcowe</h3>
                    <button 
                        type="button" 
                        className="modal-close-button" 
                        onClick={onClose}
                        title="Zamknij"
                    >
                        ×
                    </button>
                </div>
                <form className="form-group" onSubmit={handleSubmit}>
                    <label>Wybierz przedmiot:</label>
                    <select
                        name='module_id'
                        className="form-input"
                        value={module_id}
                        onChange={handleModuleChange}
                        required
                    >
                        <option value="">-- Wybierz --</option>
                        {modules.map(module => (
                            <option key={module.module_id} value={module.module_id}>
                                {module.module_name}
                            </option>
                        ))}
                    </select>

                    {module_id && groups.length > 0 && (
                        <>
                            <label style={{ marginTop: '1rem' }}>Wybierz grupę:</label>
                            <select
                                name='group_code'
                                className="form-input"
                                value={selectedGroupCode}
                                onChange={handleGroupChange}
                            >
                                <option value="">-- Wszystkie grupy --</option>
                                {groups.map(group => (
                                    <option key={group.group_code} value={group.group_code}>
                                        {group.group_name}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}

                    {module_id && students.length > 0 && (
                        <div className="final-grades-table-container">
                            <div className="final-grades-table-header">
                                <span>Kursant</span>
                                <span>Numer indeksu</span>
                                <span>Grupa</span>
                                <span>Średnia ocen</span>
                                <span>Ocena końcowa</span>
                                <span>Zaliczony</span>
                                <span>Data</span>
                            </div>
                            <div className="final-grades-table-body">
                                {students.map(student => (
                                    <div key={student.student_id} className="final-grades-table-row">
                                        <span className="student-name">
                                            {student.first_name} {student.last_name}
                                        </span>
                                        <span>{student.student_number || '—'}</span>
                                        <span>{student.group_name || '—'}</span>
                                        <span className="average-grade">
                                            {student.average_grade 
                                                ? `${parseFloat(student.average_grade).toFixed(2)}` 
                                                : '—'}
                                            {student.average_grade && (studentGrades[student.student_id]?.grade_format || 'scale_5') === 'scale_5' && (
                                                <button
                                                    type="button"
                                                    className="use-average-button"
                                                    onClick={() => {
                                                        const avgGrade = Math.floor(parseFloat(student.average_grade)).toFixed(1);
                                                        handleGradeChange(student.student_id, 'final_grade', avgGrade);
                                                    }}
                                                    title="Użyj średniej jako oceny końcowej"
                                                >
                                                    →
                                                </button>
                                            )}
                                        </span>
                                        <span>
                                            <GradeFormatFields
                                                compact
                                                className="form-input final-grade-select"
                                                format={studentGrades[student.student_id]?.grade_format || 'scale_5'}
                                                value={studentGrades[student.student_id]?.final_grade || ''}
                                                onFormatChange={(next) => handleGradeChange(student.student_id, 'grade_format', next)}
                                                onValueChange={(next) => handleGradeChange(student.student_id, 'final_grade', next)}
                                            />
                                        </span>
                                        <span>
                                            <select
                                                className="form-input final-grade-select"
                                                value={studentGrades[student.student_id]?.is_passed ? 'true' : 'false'}
                                                onChange={(e) => handleGradeChange(student.student_id, 'is_passed', e.target.value)}
                                                disabled={!studentGrades[student.student_id]?.final_grade}
                                            >
                                                <option value="false">Nie</option>
                                                <option value="true">Tak</option>
                                            </select>
                                        </span>
                                        <span>
                                            <input
                                                type='date'
                                                className="form-input final-grade-select"
                                                value={studentGrades[student.student_id]?.date || ''}
                                                onChange={(e) => handleGradeChange(student.student_id, 'date', e.target.value)}
                                                disabled={!studentGrades[student.student_id]?.final_grade}
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {module_id && students.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '1rem' }}>
                            Brak kursantów dla tego przedmiotu
                        </p>
                    )}

                    {errorBanner}

                    <div className="report-modal-buttons" style={{ marginTop: '1.5rem' }}>
                        <button 
                            type="button" 
                            className="report-modal-button report-modal-button-secondary" 
                            onClick={onClose}
                        >
                            Anuluj
                        </button>
                        <button 
                            type="submit" 
                            className="report-modal-button report-modal-button-primary"
                            disabled={!module_id || students.length === 0}
                        >
                            Zapisz oceny
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AssignmentModal;
